/**
 * features/warranties/hooks/useWarrantyPermissions.ts
 *
 * Permisos del módulo Garantías para la UI. Módulo migrado a keys finas via
 * activación POR MÓDULO (USE_FINE_PERMISSIONS_WARRANTIES), sin encender el flag
 * global. Espeja la autoridad server-side de 00327:
 *     is_owner() OR has_permission('warranties.<x>', tienda)
 * y el DINERO exige approve AND credit (warranty_can_money).
 *
 * - Flag ON  -> resuelve con my_permissions (efectivo: role_default + overrides).
 * - Flag OFF / carga fallida -> legacy: gestionar_garantias / aprobar_garantias.
 *
 * La RLS y los RPC de garantías (SECURITY DEFINER) siguen mandando aunque la UI
 * falle. Aquí sólo se decide qué mostrar/ocultar. is_owner ve todo por contrato
 * global (no se depende de role='director' como bypass visual).
 */

import { useState, useEffect } from 'react'
import { supabase } from '@lib/supabase/client'
import { usePermissions } from '@shared/hooks/usePermissions'
import { useAuth } from '@features/auth/hooks/useAuth'
import { USE_FINE_PERMISSIONS_WARRANTIES } from '@lib/permissions'

export interface WarrantyCapabilities {
  /** Ver la página/listado/detalle. */
  canView: boolean
  /** Operar el expediente: diagnóstico, prioridad, responsable, checklist, evidencia, taller, entrega, crear. */
  canManage: boolean
  /** Autorizar resoluciones sensibles: reemplazo, cierre, cancelación, cliente resuelto. */
  canApprove: boolean
  /** Enviar al proveedor y registrar su respuesta/RMA/recuperación. */
  canSupplier: boolean
  /** DINERO: saldo a favor / recuperación con crédito. Exige approve AND credit (o owner). */
  canCredit: boolean
}

export interface WarrantyPermissions extends WarrantyCapabilities {
  /** true cuando el modelo fino ya cargó (o el flag está apagado). */
  ready: boolean
}

/**
 * Resolución PURA de la autoridad visual de Garantías. Espeja el servidor (00327):
 *   canX   = is_owner OR (fino cargado ? efectivo(warranties.x) : legacy)
 *   credit = is_owner OR (approve AND credit)   [dinero: nunca sólo manage]
 * `fine` = null ⇒ modelo fino no cargado (flag off / RPC en vuelo / error) ⇒ legacy.
 */
export function resolveWarrantyCapabilities(input: {
  isOwner: boolean
  fine: Set<string> | null
  legacyManage: boolean
  legacyApprove: boolean
}): WarrantyCapabilities {
  const { isOwner, fine, legacyManage, legacyApprove } = input
  const has = (key: string, legacy: boolean): boolean => isOwner || (fine ? fine.has(key) : legacy)
  const canCredit = fine
    ? isOwner || (fine.has('warranties.approve') && fine.has('warranties.credit'))
    : isOwner || legacyApprove
  return {
    canView: has('warranties.view', legacyManage),
    canManage: has('warranties.manage', legacyManage),
    canApprove: has('warranties.approve', legacyApprove),
    canSupplier: has('warranties.supplier', legacyApprove),
    canCredit,
  }
}

export function useWarrantyPermissions(): WarrantyPermissions {
  const { can } = usePermissions() // fallback legacy
  const { cajero } = useAuth()
  const isOwner = cajero?.isOwner ?? false
  const [fineSet, setFineSet] = useState<Set<string> | null>(null)

  useEffect(() => {
    if (!USE_FINE_PERMISSIONS_WARRANTIES) return
    let cancelled = false
    void supabase.rpc('my_permissions').then(({ data, error }) => {
      if (cancelled) return
      if (error) {
        setFineSet(null)
        return
      }
      setFineSet(
        new Set(
          (data ?? [])
            .filter((r: { allowed: boolean }) => r.allowed)
            .map((r: { permission_key: string }) => r.permission_key)
        )
      )
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Mientras el fino no carga, se usa legacy para evitar parpadeo.
  const fine = USE_FINE_PERMISSIONS_WARRANTIES && fineSet !== null ? fineSet : null

  const caps = resolveWarrantyCapabilities({
    isOwner,
    fine,
    // Legacy: espeja el contrato previo (gestionar_garantias / aprobar_garantias).
    legacyManage: can('gestionar_garantias'),
    legacyApprove: can('aprobar_garantias'),
  })

  return {
    ...caps,
    ready: !USE_FINE_PERMISSIONS_WARRANTIES || fineSet !== null,
  }
}
